<div class="membership container py-4">
  @if (loading()) {
    <p>{{ 'membership.loading' | translate }}</p>
  } @else {
    <!-- Step indicator -->
    <div class="membership__steps">
      @if (upgradeMode()) {
        <!-- The upgrade buys nothing but the difference: no plan choice, no details
             to re-enter and no health declaration, so those labels would only
             promise steps that never come. -->
        <span [class.active]="step() === 'upgrade'">{{ 'membership.steps.upgrade' | translate }}</span>
      } @else if (specialUpgradeMode()) {
        <span [class.active]="step() === 'special_upgrade'">{{ 'membership.special_upgrade.step' | translate }}</span>
      } @else if (practitionerRenewalMode()) {
        <span [class.active]="step() === 'practitioner_renewal'">{{ 'membership.practitioner_renewal.step' | translate }}</span>
      } @else {
        <span [class.active]="step() === 'plan'">{{ 'membership.steps.plan' | translate }}</span>
        <span [class.active]="step() === 'info'">{{ 'membership.steps.info' | translate }}</span>
        @if (!isClub()) {
          <span [class.active]="step() === 'notice' || step() === 'declaration'">{{ 'membership.steps.health' | translate }}</span>
        }
      }
      <span [class.active]="step() === 'payment'">{{ 'membership.steps.payment' | translate }}</span>
    </div>

    @if (error()) {
      <div class="alert alert-danger" role="alert">{{ error() }}</div>
    }

    <!-- Regular SPECIAL, member also eligible for the upgrade (rule C of 2026-10-19):
         point out the cheaper way; price and regular price are the server's offer. -->
    @if (specialUpgradeHintVisible() && specialUpgradeOffer(); as su) {
      <div class="alert alert-info d-flex flex-column flex-md-row align-items-md-center gap-2" role="note">
        <span class="flex-grow-1">
          {{ 'membership.special.wizard_upgrade_hint' | translate:{ price: specialAmount(su.total_price), regular: specialAmount(su.regular_price ?? bundlePlan()?.price) } }}
        </span>
        <button cButton color="primary" size="sm" (click)="switchToSpecialUpgrade()">
          {{ 'membership.special.wizard_upgrade_cta' | translate }}
        </button>
      </div>
    }

    <!-- ===== Step: plan selection ===== -->
    @if (step() === 'plan') {
      @if (bundleMode()) {
      <!-- DNA & Health bundle (Website V2): ONE card, no rhythm choice — the member
           picked this package on its sales page. Then the usual details -> payment.
           Wording rule: a SATISFACTION guarantee, never an investment or a return. -->
      <h1>{{ (familyMode() ? 'membership.family.title' : 'membership.bundle.title') | translate }}</h1>
      <p class="membership__plan-subtitle">{{ 'membership.bundle.subtitle' | translate }}</p>
      @if (bundlePlan(); as b) {
        <div class="membership__plans">
          <div class="membership__plan membership__plan--bundle selected">
            @if (b.photo_url) {
              <img class="membership__plan-img" [src]="b.photo_url" [alt]="b.name" />
            }
            <h3>{{ ('gh_product.' + bundleProductKey() + '.name') | translate }}</h3>
            <p class="membership__plan-tagline">{{ ('gh_product.' + bundleProductKey() + '.tagline') | translate }}</p>
            <!-- The central promise before the component list (customer, 2026-11-03). -->
            <p class="membership__plan-promise fw-semibold">{{ 'gh_product.dna_health_special.promise' | translate }}</p>

            <!-- The five components, in the customer's wording (decision A.5). -->
            <ul class="membership__plan-features membership__plan-components">
              @for (part of ['dna', 'nutrition', 'fitness', 'dr_future', 'membership']; track part) {
                <li>
                  <strong>{{ ('gh_product.dna_health_special.components.' + part + '.title') | translate }}</strong>
                  — {{ ('gh_product.dna_health_special.components.' + part + '.text') | translate }}
                </li>
              }
            </ul>

            <div class="membership__plan-price">${{ b.price | number:'1.2-2' }}</div>
            <div class="membership__plan-price-month">
              {{ (paymentTestMode ? 'membership.plan.test_price_note' : 'membership.bundle.one_time') | translate }}
            </div>

            @if (familyMode()) {
              <!-- DNA Family SPECIAL: in place of the guarantee box, the plain notice
                   that this package carries NO voluntary money-back guarantee. -->
              <div class="alert alert-warning membership__no-guarantee" role="note">
                <strong>{{ 'gh_product.dna_family_special.no_guarantee' | translate }}</strong>
                <p class="small mb-0">{{ 'membership.family.notice' | translate }}</p>
              </div>
            } @else if (bundleHasGuarantee()) {
              <div class="membership__bundle-guarantee">
                <strong>{{ 'membership.guarantee.headline' | translate }}</strong>
                <p>{{ 'membership.guarantee.summary' | translate }}</p>
                <p class="small mb-0">{{ 'membership.guarantee.terms' | translate }}</p>
              </div>
            }
            <!-- Customer decision 2026-10-01: who the member contracts with for the package. -->
            <p class="membership__hint small mb-0">{{ 'membership.bundle.contracting_party' | translate }}</p>
          </div>
        </div>
      }
      } @else {
      <h1>{{ 'membership.plan.title' | translate }}</h1>
      <p class="membership__plan-subtitle">
        {{ (isClub() ? 'membership.plan.subtitle_club' : 'membership.plan.subtitle_protection') | translate }}
      </p>
      <!-- One card per PRODUCT (WolfPack Global Health Membership / WolfPack
           Practitioner Membership); its rhythms are duration OPTIONS of that one
           product, never products of their own (customer decision 2026-10-02). -->
      <div class="membership__plans">
        @for (group of planTierGroups(); track group.tier) {
          <div class="membership__plan"
               [class.selected]="groupSelected(group)">
            @if (group.plans[0].photo_url) {
              <img class="membership__plan-img" [src]="group.plans[0].photo_url" [alt]="('gh_product.' + group.product + '.name') | translate" />
            }
            <h3>{{ ('gh_product.' + group.product + '.name') | translate }}</h3>
            <p class="membership__plan-tagline">{{ ('gh_product.' + group.product + '.tagline') | translate }}</p>
            @if (group.tier === 'practitioner') {
              <p class="membership__tier-founding">{{ 'membership.tier.founding_note' | translate }}</p>
            }

            <ul class="membership__plan-features">
              @for (feature of planFeatures(group.plans[0].code); track feature) {
                <li>{{ feature }}</li>
              }
            </ul>

            <div class="membership__options">
              @for (p of group.plans; track p.code) {
                <label class="membership__radio-row">
                  <input type="radio" name="plan" [checked]="selectedPlan() === p.code" (change)="choosePlan(p.code)" />
                  <span>{{ planOptionLabel(p) }}</span>
                </label>
              }
            </div>
            @if (paymentTestMode) {
              <div class="membership__plan-price-month">{{ 'membership.plan.test_price_note' | translate }}</div>
            }
          </div>
        }
      </div>
      }

      <!-- Weekly rhythm: the customer prepays 1..39 weeks in one order. Shown only
           for a week-billed plan; every other plan has a fixed term. -->
      @if (isWeeklyPlan()) {
        <div class="membership__panel membership__weeks">
          <h4>{{ 'membership.plan.weeks_title' | translate }}</h4>
          <p class="membership__weeks-hint">{{ 'membership.plan.weeks_hint' | translate }}</p>

          <label class="membership__weeks-row">
            <span class="membership__field-label">{{ 'membership.plan.weeks_label' | translate }}</span>
            <select name="weeks" [ngModel]="weeks()" (ngModelChange)="setWeeks($event)">
              @for (w of weekOptions(); track w) {
                <option [ngValue]="w">{{ w }}</option>
              }
            </select>
          </label>

          <div class="membership__weeks-total">
            <span>{{ 'membership.plan.weeks_total' | translate }}</span>
            <strong>${{ weeklyTotal() | number:'1.2-2' }}</strong>
          </div>
        </div>
      }

      <!-- Coverage is still running, so this purchase is ADDED to it: name the
           current end date and the one it leads to before the member pays. The
           server computes the real date from the same base (renewal_base_date). -->
      @if (extensionBase()) {
        <div class="membership__panel membership__extends">
          <p>{{ extensionNoteKey() | translate:{ until: extensionUntil(), weeks: weeks(), new_end: extensionNewEnd() } }}</p>
          @if (showPractitionerSwitchNote()) {
            <p>{{ 'membership.renewal.practitioner_switch_note' | translate }}</p>
          }
        </div>
      }

      @if (planDiffersFromRecommendation()) {
        <div class="alert alert-warning mt-3" role="alert">{{ 'membership.plan.differs_warning' | translate }}</div>
      }

      <button cButton color="primary" class="mt-3" [disabled]="!selectedPlan() || busy()" (click)="confirmPlan()">
        {{ 'membership.continue' | translate }}
      </button>
    }

    <!-- ===== Step: customer info ===== -->
    @if (step() === 'info') {
      @if (!upgradeMode() && selectedLabel()) {
        <!-- What is being bought, named as the buyer knows it; "change" reopens the
             plan step (it was skipped when the plan arrived preselected). -->
        <div class="membership__panel membership__summary">
          <span>{{ 'membership.summary.product' | translate }}: <strong>{{ selectedLabel() }}</strong></span>
          <button class="membership__link" type="button" (click)="changePlan()">{{ 'membership.summary.change' | translate }}</button>
        </div>
      }
      <h1>{{ 'membership.info.title' | translate }}</h1>
      <form class="membership__form" (ngSubmit)="submitInfo()" novalidate>
        <p class="membership__required-hint">
          <span class="membership__req">*</span> {{ 'membership.info.required_hint' | translate }}
        </p>
        <div class="membership__grid">
          <label><span class="membership__field-label">{{ 'membership.info.first_name' | translate }} <span class="membership__req">*</span></span>
            <input type="text" name="first_name" autocomplete="given-name" required [(ngModel)]="info.first_name" />
          </label>
          <label><span class="membership__field-label">{{ 'membership.info.last_name' | translate }} <span class="membership__req">*</span></span>
            <input type="text" name="last_name" autocomplete="family-name" required [(ngModel)]="info.last_name" />
          </label>
          <label><span class="membership__field-label">{{ 'membership.info.date_of_birth' | translate }} <span class="membership__req">*</span></span>
            <input type="date" name="dob" required [(ngModel)]="info.date_of_birth" />
          </label>
          <label><span class="membership__field-label">{{ 'membership.info.phone' | translate }} <span class="membership__req">*</span></span>
            <input type="tel" name="phone" required [(ngModel)]="info.phone" />
          </label>
          <label class="membership__full"><span class="membership__field-label">{{ 'membership.info.address' | translate }} <span class="membership__req">*</span></span>
            <input type="text" name="res_address" required [(ngModel)]="info.res_address" />
          </label>
          <label><span class="membership__field-label">{{ 'membership.info.city' | translate }} <span class="membership__req">*</span></span>
            <input type="text" name="res_city" required [(ngModel)]="info.res_city" />
          </label>
          <label><span class="membership__field-label">{{ 'membership.info.country' | translate }} <span class="membership__req">*</span></span>
            <select name="res_country" required [(ngModel)]="info.res_country" (ngModelChange)="onCountryChange()">
              <option value="">{{ 'membership.info.select_placeholder' | translate }}</option>
              @for (c of countries(); track c.code) {
                <option [value]="c.code">{{ c.name }}</option>
              }
            </select>
          </label>
          <label>{{ 'membership.info.state' | translate }}
            @if (statesForCountry().length) {
              <select name="res_state" [(ngModel)]="info.res_state">
                <option value="">{{ 'membership.info.select_placeholder' | translate }}</option>
                @for (s of statesForCountry(); track s.code) {
                  <option [value]="s.code">{{ s.name }}</option>
                }
              </select>
            } @else {
              <input type="text" name="res_state" [(ngModel)]="info.res_state" />
            }
          </label>
          <label><span class="membership__field-label">{{ 'membership.info.postal_code' | translate }} <span class="membership__req">*</span></span>
            <input type="text" name="res_postal_code" required [(ngModel)]="info.res_postal_code" />
          </label>
          <label class="membership__full">{{ 'membership.info.ssn' | translate }}
            <input type="text" name="ssn" [(ngModel)]="info.ssn" />
          </label>
        </div>

        <label class="membership__checkbox">
          <input type="checkbox" name="billing_same" [(ngModel)]="info.billing_same_as_residential" />
          {{ 'membership.info.billing_same' | translate }}
        </label>

        @if (!info.billing_same_as_residential) {
          <h4 class="membership__subhead">{{ 'membership.info.billing_title' | translate }}</h4>
          <div class="membership__grid">
            <label class="membership__full"><span class="membership__field-label">{{ 'membership.info.address' | translate }} <span class="membership__req">*</span></span>
              <input type="text" name="bill_address" autocomplete="billing street-address" required [(ngModel)]="info.bill_address" />
            </label>
            <label><span class="membership__field-label">{{ 'membership.info.city' | translate }} <span class="membership__req">*</span></span>
              <input type="text" name="bill_city" required [(ngModel)]="info.bill_city" />
            </label>
            <label><span class="membership__field-label">{{ 'membership.info.country' | translate }} <span class="membership__req">*</span></span>
              <select name="bill_country" required [(ngModel)]="info.bill_country" (ngModelChange)="onBillingCountryChange()">
                <option value="">{{ 'membership.info.select_placeholder' | translate }}</option>
                @for (c of countries(); track c.code) {
                  <option [value]="c.code">{{ c.name }}</option>
                }
              </select>
            </label>
            <label>{{ 'membership.info.state' | translate }}
              @if (statesForBillingCountry().length) {
                <select name="bill_state" [(ngModel)]="info.bill_state">
                  <option value="">{{ 'membership.info.select_placeholder' | translate }}</option>
                  @for (s of statesForBillingCountry(); track s.code) {
                    <option [value]="s.code">{{ s.name }}</option>
                  }
                </select>
              } @else {
                <input type="text" name="bill_state" [(ngModel)]="info.bill_state" />
              }
            </label>
            <label><span class="membership__field-label">{{ 'membership.info.postal_code' | translate }} <span class="membership__req">*</span></span>
              <input type="text" name="bill_postal_code" required [(ngModel)]="info.bill_postal_code" />
            </label>
          </div>
        }

        <div class="membership__actions">
          <button cButton color="secondary" type="button" (click)="changePlan()">{{ 'membership.back' | translate }}</button>
          <button cButton color="primary" type="submit" [disabled]="!infoValid() || busy()">{{ 'membership.continue' | translate }}</button>
        </div>
      </form>
    }

    <!-- ===== Step: translation notice (English-only) ===== -->
    @if (step() === 'notice') {
      <h1>{{ 'membership.notice.title' | translate }}</h1>
      <div class="membership__panel">
        <p>{{ 'membership.notice.p1' | translate }}</p>
        <p><strong>{{ 'membership.notice.p2' | translate }}</strong></p>
        <p>{{ 'membership.notice.p3' | translate }}</p>

        <h4>{{ 'membership.notice.how_title' | translate }}</h4>
        <p class="membership__notice-sub">{{ 'membership.notice.chrome_title' | translate }}</p>
        <ol class="membership__notice-steps">
          <li>{{ 'membership.notice.chrome_1' | translate }}</li>
          <li>{{ 'membership.notice.chrome_2' | translate }}</li>
          <li>{{ 'membership.notice.chrome_3' | translate }}</li>
          <li>{{ 'membership.notice.chrome_4' | translate }}</li>
        </ol>
        <p class="membership__notice-sub">{{ 'membership.notice.mobile_title' | translate }}</p>
        <ol class="membership__notice-steps">
          <li>{{ 'membership.notice.mobile_1' | translate }}</li>
          <li>{{ 'membership.notice.mobile_2' | translate }}</li>
          <li>{{ 'membership.notice.mobile_3' | translate }}</li>
          <li>{{ 'membership.notice.mobile_4' | translate }}</li>
        </ol>

        <p class="membership__notice-legal">
          <strong>{{ 'membership.notice.important_title' | translate }}:</strong>
          {{ 'membership.notice.disclaimer' | translate }}
        </p>

        <label class="membership__checkbox">
          <input type="checkbox" [checked]="noticeChecked()" (change)="noticeChecked.set($any($event.target).checked)" />
          {{ 'membership.notice.checkbox' | translate }}
        </label>

        <div class="membership__actions">
          <button cButton color="secondary" type="button" (click)="back('info')">{{ 'membership.back' | translate }}</button>
          <button cButton color="primary" type="button" [disabled]="busy() || !noticeChecked()" (click)="acceptNotice()">
            {{ 'membership.notice.accept' | translate }}
          </button>
        </div>
      </div>
    }

    <!-- ===== Step: health declaration (English only) ===== -->
    @if (step() === 'declaration') {
      <h1>{{ 'membership.declaration.title' | translate }}</h1>
      <p class="membership__english-note">{{ 'membership.declaration.english_only' | translate }}</p>

      @if (locked()) {
        <div class="alert alert-secondary">{{ 'membership.declaration.locked' | translate }}</div>
      }

      <div class="membership__panel">
        @for (q of declQuestions(); track q.key) {
          <div class="membership__decl-q">
            <p class="membership__decl-text">{{ q.text }}</p>
            <div class="membership__decl-options">
              <label><input type="radio" [name]="q.key" [disabled]="locked()" [checked]="declAnswers[q.key] === 'yes'" (change)="answerQuestion(q, 'yes')" /> {{ 'membership.declaration.yes' | translate }}</label>
              <label><input type="radio" [name]="q.key" [disabled]="locked()" [checked]="declAnswers[q.key] === 'no'" (change)="answerQuestion(q, 'no')" /> {{ 'membership.declaration.no' | translate }}</label>
              @if (q.type === 'yes_no_na') {
                <label><input type="radio" [name]="q.key" [disabled]="locked()" [checked]="declAnswers[q.key] === 'not_applicable'" (change)="answerQuestion(q, 'not_applicable')" /> {{ 'membership.declaration.not_applicable' | translate }}</label>
              }
            </div>
            @if (q.follow_up && declAnswers[q.key] === 'yes') {
              <label class="membership__decl-followup">
                {{ q.follow_up.label }}
                <input
                  type="text"
                  [disabled]="locked()"
                  [value]="declAnswers[q.follow_up.key] || ''"
                  (input)="setDeclAnswer(q.follow_up.key, $any($event.target).value)"
                />
              </label>
            }
          </div>
        }

        @if (!locked()) {
          <label class="membership__checkbox">
            <input type="checkbox" [checked]="ack1()" (change)="ack1.set($any($event.target).checked)" />
            {{ 'membership.declaration.ack_truthful' | translate }}
          </label>
          <label class="membership__checkbox">
            <input type="checkbox" [checked]="ack2()" (change)="ack2.set($any($event.target).checked)" />
            {{ 'membership.declaration.ack_false_info' | translate }}
          </label>
          <label class="membership__checkbox">
            <input type="checkbox" [checked]="ack3()" (change)="ack3.set($any($event.target).checked)" />
            {{ 'membership.declaration.ack_underwriting' | translate }}
          </label>
        }

        @if (underwritingStatus() === 'TEMPORARY_DECLINE') {
          <div class="alert alert-warning mt-3">
            {{ 'membership.declaration.temporary_decline' | translate }}
            <button cButton color="primary" size="sm" class="mt-2" type="button" (click)="requestReview()">
              {{ 'membership.declaration.request_review' | translate }}
            </button>
          </div>
        } @else if (underwritingStatus() === 'MANUAL_REVIEW_REQUESTED') {
          <div class="alert alert-info mt-3">{{ 'membership.declaration.manual_review_requested' | translate }}</div>
        } @else if (underwritingStatus() === 'MANUAL_REVIEW') {
          <div class="alert alert-info mt-3">{{ 'membership.declaration.manual_review' | translate }}</div>
        } @else if (underwritingStatus() === 'DECLINED') {
          <div class="alert alert-danger mt-3">{{ 'membership.declaration.declined' | translate }}</div>
        }

        <div class="membership__actions">
          @if (!locked()) {
            <button cButton color="secondary" type="button" (click)="back('notice')">{{ 'membership.back' | translate }}</button>
            <button cButton color="primary" type="button" [disabled]="!declarationComplete() || busy()" (click)="submitDeclaration()">
              {{ 'membership.declaration.submit' | translate }}
            </button>
          } @else if (underwritingStatus() === 'APPROVED') {
            <button cButton color="primary" type="button" (click)="step.set('payment')">
              {{ 'membership.declaration.continue_payment' | translate }}
            </button>
          }
        </div>
      </div>
    }

    <!-- ===== Step: upgrade to the DNA & Health SPECIAL (decision D of 2026-10-03) ===== -->
    <!-- Reached only via ?plan=GH_DNA_HEALTH_SPECIAL_UPGRADE AND a server-issued
         special_upgrade_offer. ONE card: the five components, the credit, the
         guarantee and the fresh 12 months. -->
    @if (step() === 'special_upgrade') {
      @if (specialUpgradeOffer(); as su) {
        <h1>{{ 'membership.special_upgrade.title' | translate }}</h1>
        <p class="membership__plan-subtitle">{{ 'membership.special_upgrade.subtitle' | translate }}</p>

        <div class="membership__panel membership__upgrade">
          <p class="fw-semibold">{{ 'gh_product.dna_health_special.promise' | translate }}</p>
          <ul class="mb-3 ps-3">
            @for (part of specialComponents; track part) {
              <li>
                <strong>{{ ('gh_product.dna_health_special.components.' + part + '.title') | translate }}</strong>
                — {{ ('gh_product.dna_health_special.components.' + part + '.text') | translate }}
              </li>
            }
          </ul>
          @if (su.regular_price) {
            <p class="mb-1">{{ 'membership.special_upgrade.regular_price' | translate:{ price: specialAmount(su.regular_price) } }}</p>
          }
          <p class="mb-1">{{ 'membership.special_upgrade.credit_line' | translate:{ credit: specialAmount(su.credit) } }}</p>
          <p class="membership__upgrade-price">
            <strong>{{ 'membership.special_upgrade.price' | translate:{ price: specialAmount(su.total_price) } }}</strong>
          </p>
          <p class="mb-1">{{ 'membership.special_upgrade.guarantee_line' | translate:{ amount: specialAmount(su.guarantee_amount) } }}</p>
          <p class="mb-1">{{ 'membership.special_upgrade.fresh_term' | translate }}</p>
          @if (su.practitioner) {
            <p class="mb-1">{{ 'membership.special_upgrade.practitioner_kept' | translate }}</p>
          }
          @if (paymentTestMode) {
            <p class="membership__hint">{{ 'membership.plan.test_price_note' | translate }}</p>
          }

          <div class="membership__actions">
            <button cButton color="primary" type="button" [disabled]="busy()" (click)="confirmSpecialUpgrade()">
              {{ 'membership.special_upgrade.cta' | translate }}
            </button>
            <button cButton color="secondary" type="button" (click)="goToDashboard()">
              {{ 'membership.back' | translate }}
            </button>
          </div>
        </div>
      }
    }

    <!-- ===== Step: Practitioner Membership on a running SPECIAL (decisions of 2026-10-04) ===== -->
    <!-- Reached only via ?practitioner_renewal=1 AND a server-issued
         practitioner_renewal_offer — for EVERY SPECIAL member. Sets or extends only
         the Practitioner status; the SPECIAL's term and plan stay exactly as they
         are. A member who never was a Practitioner has no retained_until, so
         neither status line shows and the new term starts at the purchase. -->
    @if (step() === 'practitioner_renewal') {
      @if (practitionerRenewalOffer(); as pr) {
        <h1>{{ 'membership.practitioner_renewal.title' | translate }}</h1>
        <p class="membership__plan-subtitle">{{ 'membership.practitioner_renewal.subtitle' | translate }}</p>

        <div class="membership__panel membership__upgrade">
          @if (pr.retained && pr.retained_until) {
            <p class="mb-2">{{ 'membership.practitioner_renewal.current_until' | translate:{ date: formatDate(pr.retained_until) } }}</p>
          } @else if (pr.retained_until) {
            <p class="mb-2">{{ 'membership.practitioner_renewal.ended_on' | translate:{ date: formatDate(pr.retained_until) } }}</p>
          }
          @for (plan of pr.plans; track plan.code) {
            <label class="membership__radio-row">
              <input type="radio" name="renewalPlan" [checked]="renewalPlan() === plan.code" (change)="chooseRenewalPlan(plan.code)" />
              <span>{{ (plan.billing_period === 'week' ? 'membership.practitioner_renewal.option_week' : 'membership.practitioner_renewal.option_year')
                       | translate:{ price: renewalAmount(plan.unit_price), min: pr.min_weeks, max: pr.max_weeks } }}</span>
            </label>
          }
          @if (renewalWeekly()) {
            <label class="form-label mt-2" for="renewalWeeks">{{ 'membership.practitioner_renewal.weeks' | translate }}</label>
            <select id="renewalWeeks" class="form-select mb-2" style="max-width: 10rem"
                    [ngModel]="weeks()" (ngModelChange)="setWeeks($event)">
              @for (w of weekOptions(); track w) { <option [ngValue]="w">{{ w }}</option> }
            </select>
          }
          <p class="membership__upgrade-price">
            <strong>{{ 'membership.practitioner_renewal.price' | translate:{ price: renewalAmount(renewalTotal()) } }}</strong>
          </p>
          @if (renewalNewEnd()) {
            <p class="mb-1">{{ 'membership.practitioner_renewal.new_end' | translate:{ date: renewalNewEnd() } }}</p>
          }
          @if (pr.special_paid_through) {
            <p class="mb-1">{{ 'membership.practitioner_renewal.special_unchanged' | translate:{ date: formatDate(pr.special_paid_through) } }}</p>
          }
          @if (paymentTestMode) {
            <p class="membership__hint">{{ 'membership.plan.test_price_note' | translate }}</p>
          }

          <div class="membership__actions">
            <button cButton color="primary" type="button" [disabled]="busy() || !renewalPlanInfo()" (click)="confirmPractitionerRenewal()">
              {{ 'membership.practitioner_renewal.cta' | translate }}
            </button>
            <button cButton color="secondary" type="button" (click)="goToDashboard()">
              {{ 'membership.back' | translate }}
            </button>
          </div>
        </div>
      }
    }

    <!-- ===== Step: Practitioner Network upgrade ===== -->
    <!-- Reached only via ?upgrade=1 AND a server-issued upgrade_offer. Everything
         shown here is the server's offer: the same package the member already paid
         for, priced at the difference. Nothing about it is chosen again. -->
    @if (step() === 'upgrade') {
      @if (upgradeOffer(); as offer) {
        <h1>{{ 'membership.upgrade.title' | translate }}</h1>
        <p class="membership__plan-subtitle">{{ 'membership.upgrade.subtitle' | translate }}</p>

        <div class="membership__panel membership__upgrade">
          <p class="membership__upgrade-fromto">
            {{ 'membership.upgrade.from_to' | translate:{
                 from: planName(offer.from_plan), to: planName(offer.to_plan) } }}
          </p>
          <p class="membership__upgrade-package">
            {{ upgradePackageKey() | translate:{ weeks: offer.quantity } }}
          </p>
          <p class="membership__upgrade-price">
            <strong>{{ 'membership.upgrade.price' | translate:{ price: (upgradeTotal() | number:'1.2-2') } }}</strong>
          </p>
          @if (paymentTestMode) {
            <!-- dev/staging: the backend divides by CommissionService.amount_divisor,
                 so the offer total is already the amount that will be charged. -->
            <p class="membership__hint">{{ 'membership.plan.test_price_note' | translate }}</p>
          }

          <label class="membership__checkbox">
            <input type="checkbox" [checked]="upgradeWaiver()"
                   (change)="upgradeWaiver.set($any($event.target).checked)" />
            {{ 'membership.upgrade.waiver' | translate }}
          </label>

          <div class="membership__actions">
            <button cButton color="primary" type="button" [disabled]="!upgradeWaiver() || busy()" (click)="confirmUpgrade()">
              {{ 'membership.upgrade.cta' | translate }}
            </button>
            <button cButton color="secondary" type="button" (click)="goToDashboard()">
              {{ 'membership.back' | translate }}
            </button>
          </div>
        </div>
      }
    }

    <!-- ===== Step: payment ===== -->
    @if (step() === 'payment') {
      <h1>{{ 'membership.payment.title' | translate }}</h1>

      @if (blockingOrder(); as open) {
        <!-- A payment is already running that this wizard cannot silently
             replace. Name it, then offer both ways out — a customer who no
             longer remembers starting it can act on neither otherwise. -->
        <div class="membership__panel membership__pending">
          <h4>{{ 'membership.payment.pending_title' | translate }}</h4>
          <p>
            {{ 'membership.payment.pending_body' | translate:{
                 method: ('membership.payment.method.' + open.payment_method) | translate,
                 total: open.total_with_costs,
                 currency: open.currency } }}
          </p>
          @if (open.bank_transfer_reference) {
            <p class="membership__hint">
              {{ 'membership.payment.pending_reference' | translate:{ reference: open.bank_transfer_reference } }}
            </p>
          }
          @if (open.can_cancel === false) {
            <p class="membership__hint">{{ 'membership.payment.pending_uncancelable' | translate }}</p>
          }
          <div class="membership__actions">
            <button cButton color="primary" type="button" (click)="resumeBlockingOrder()">
              {{ 'membership.payment.pending_continue' | translate }}
            </button>
            @if (open.can_cancel !== false) {
              <button cButton color="secondary" type="button" [disabled]="busy()" (click)="cancelBlockingOrder()">
                {{ 'membership.payment.pending_cancel' | translate }}
              </button>
            }
          </div>
        </div>
      } @else if (orderId()) {
        <!-- A real gateway checkout was started — show its confirmation UI.
             Works in dev too, so the live PayPal sandbox flow is testable. -->
        @switch (paymentMethod()) {
          @case ('ewallet') {
            <app-ewallet-confirmation
              [orderId]="orderId()"
              [totalPrice]="totalPrice()"
              (onSuccess)="onPaymentSuccess()"
              (onBack)="orderId.set(null)" />
          }
          @case ('web3_payment') {
            <!-- Without a recipient (initiate_payment failed) ethers reads "" as an
                 ENS name and throws UNCONFIGURED_NAME on send — never render it empty. -->
            @if (recipientAddress()) {
              <app-web3-confirmation
                [orderId]="orderId()"
                [totalPrice]="totalPrice()"
                [recipientAddress]="recipientAddress()"
                (onSuccess)="onPaymentSuccess()"
                (onBack)="orderId.set(null)" />
            }
          }
          @case ('paypal') {
            <app-paypal-confirmation
              [orderId]="orderId()"
              [totalPrice]="totalPrice()"
              [paymentCosts]="paymentCosts()"
              [totalWithCosts]="totalWithCosts()"
              (onSuccess)="onPaymentSuccess()"
              (onFailed)="onGatewayFailed($event)"
              (onBack)="orderId.set(null)" />
          }
          @case ('bank_transfer') {
            <app-bank-transfer-confirmation
              [orderId]="orderId()"
              [totalPrice]="totalPrice()"
              [cancelConfirmMessage]="'membership.payment.bank_transfer_cancel_confirm' | translate"
              (onSuccess)="onPaymentSuccess()"
              (onCancel)="cancelBankTransfer()" />
          }
        }
      } @else if (liveMethods().length > 0) {
        @if (gatewayFailed()) {
          <!-- PayPal ended without a payment: the order is kept (same order, same
               package, same details) and is moved to whichever method is picked below. -->
          <div class="alert alert-warning" role="alert">
            <strong>{{ 'membership.payment.gateway_failed_title' | translate }}</strong>
            <p class="mb-0">{{ 'membership.payment.gateway_failed_body' | translate }}</p>
          </div>
        }
        @if (upgradeMode()) {
          <!-- The upgrade's own summary: the member is not buying a term here, they
               are paying the difference on the one they already hold. -->
          @if (upgradeOffer(); as offer) {
            <div class="membership__panel membership__upgrade-summary">
              <h4>{{ 'membership.upgrade.title' | translate }}</h4>
              <p>{{ 'membership.upgrade.from_to' | translate:{
                     from: planName(offer.from_plan), to: planName(offer.to_plan) } }}</p>
              <p>{{ upgradePackageKey() | translate:{ weeks: offer.quantity } }}</p>
              <p><strong>{{ 'membership.upgrade.price' | translate:{ price: (upgradeTotal() | number:'1.2-2') } }}</strong></p>
            </div>
          }
        } @else if (practitionerRenewalMode()) {
          @if (renewalPlanInfo(); as rp) {
            <div class="membership__panel membership__upgrade-summary">
              <h4>{{ 'membership.practitioner_renewal.title' | translate }}</h4>
              <p>{{ (rp.billing_period === 'week' ? 'membership.practitioner_renewal.summary_weeks' : 'membership.practitioner_renewal.summary_year')
                    | translate:{ weeks: weeks() } }}</p>
              <p><strong>{{ 'membership.practitioner_renewal.price' | translate:{ price: renewalAmount(renewalTotal()) } }}</strong></p>
              @if (renewalNewEnd()) { <p class="small">{{ 'membership.practitioner_renewal.new_end' | translate:{ date: renewalNewEnd() } }}</p> }
            </div>
          }
        } @else if (specialUpgradeMode()) {
          @if (specialUpgradeOffer(); as su) {
            <div class="membership__panel membership__upgrade-summary">
              <h4>{{ 'membership.special_upgrade.title' | translate }}</h4>
              <p>{{ 'membership.special_upgrade.credit_line' | translate:{ credit: specialAmount(su.credit) } }}</p>
              <p><strong>{{ 'membership.special_upgrade.price' | translate:{ price: specialAmount(su.total_price) } }}</strong></p>
              <p class="small">{{ 'membership.special_upgrade.fresh_term' | translate }}</p>
            </div>
          }
        } @else if (selectedPlanInfo()) {
          <!-- The product and its duration are summarised here, not only on the plan
               card, so a customer who walked back and forth cannot pay for a
               different number of weeks than the summary they last saw. -->
          <div class="membership__panel membership__weeks-summary">
            <h4>{{ 'membership.summary.product' | translate }}</h4>
            <p>{{ selectedLabel() }}</p>
            <p><strong>${{ dueTotal() | number:'1.2-2' }}</strong></p>
            <button class="membership__link" type="button" (click)="changePlan()">
              {{ 'membership.summary.change' | translate }}
            </button>
          </div>
        }

        <!-- Same statement on the summary the member actually pays from: whatever
             they walked through, the last screen before the money says the term is
             added to the coverage they already hold. -->
        @if (extensionBase()) {
          <div class="membership__panel membership__extends">
            <p>{{ extensionNoteKey() | translate:{ until: extensionUntil(), weeks: weeks(), new_end: extensionNewEnd() } }}</p>
            @if (showPractitionerSwitchNote()) {
              <p>{{ 'membership.renewal.practitioner_switch_note' | translate }}</p>
            }
          </div>
        }

        @if (familyCheckout()) {
          <!-- DNA Family SPECIAL: the acknowledgement is required before any payment
               method can be used (server: 422 family_terms_required). -->
          <div class="membership__panel membership__family-terms">
            <h4>{{ 'membership.family.terms_title' | translate }}</h4>
            <div class="alert alert-warning mb-2" role="note">{{ 'membership.family.notice' | translate }}</div>
            <label class="membership__checkbox">
              <input type="checkbox" [checked]="familyTermsAck()" (change)="familyTermsAck.set($any($event.target).checked)" />
              {{ 'membership.family.terms_checkbox' | translate }}
            </label>
            @if (!familyTermsAck()) {
              <p class="membership__hint small mb-0">{{ 'membership.family.terms_required' | translate }}</p>
            }
          </div>
        }

        <div class="membership__panel">
          <!-- Standard vs immediate activation is a first-purchase decision: the
               upgrade takes effect on a membership whose coverage is already
               running, so there is no start date to choose and no 14-day window to
               waive (its own acknowledgement was given on the upgrade step). -->
          @if (!upgradeMode() && !practitionerRenewalMode()) {
          <h4>{{ 'membership.payment.activation_title' | translate }}</h4>
          <label class="membership__radio-row">
            <input type="radio" name="startMode" [checked]="startMode() === 'standard'" (change)="startMode.set('standard')" />
            <span>{{ 'membership.payment.standard' | translate }}</span>
          </label>
          <label class="membership__radio-row">
            <input type="radio" name="startMode" [checked]="startMode() === 'immediate'" (change)="startMode.set('immediate')" />
            <span>{{ 'membership.payment.immediate' | translate }}</span>
          </label>

          @if (startMode() === 'immediate') {
            <div class="membership__waiver">
              <p class="membership__waiver-intro">{{ 'membership.payment.waiver.intro' | translate }}</p>
              <label class="membership__checkbox">
                <input type="checkbox" [checked]="waiver1()" (change)="waiver1.set($any($event.target).checked)" />
                {{ 'membership.payment.waiver.ack1' | translate }}
              </label>
              <label class="membership__checkbox">
                <input type="checkbox" [checked]="waiver2()" (change)="waiver2.set($any($event.target).checked)" />
                {{ 'membership.payment.waiver.ack2' | translate }}
              </label>
              <label class="membership__checkbox">
                <input type="checkbox" [checked]="waiver3()" (change)="waiver3.set($any($event.target).checked)" />
                {{ 'membership.payment.waiver.ack3' | translate }}
              </label>
              @if (!waiverAccepted()) {
                <div class="alert alert-warning membership__waiver-hint" role="status">
                  {{ 'membership.payment.waiver.incomplete' | translate }}
                </div>
              }
            </div>
          }
          }

          <!-- Real payment gateways live for this frontend (e.g. PayPal). -->
          @if (liveMethods().length > 0) {
            <h4 class="mt-3">{{ 'membership.payment.method_title' | translate }}</h4>
            @for (m of liveMethods(); track m) {
              <label class="membership__radio-row" [class.membership__radio-row--disabled]="methodDisabled(m)">
                <input type="radio" name="method" [checked]="paymentMethod() === m"
                       [disabled]="methodDisabled(m)" (change)="paymentMethod.set(m)" />
                <span>{{ ('membership.payment.method.' + m) | translate }}</span>
              </label>
              @if (methodDisabled(m)) {
                <!-- The e-wallet cannot part-pay an order, so say what is missing
                     instead of letting the customer start a payment that can
                     never be confirmed. -->
                <p class="membership__hint">
                  {{ 'membership.payment.ewallet_insufficient' | translate:{
                       balance: ewalletBalance(), total: dueTotal(), currency: ewalletCurrency() } }}
                </p>
              }
            }
            <div class="membership__actions">
              <button cButton color="primary" type="button" [disabled]="!canPay()" (click)="startCheckout()">
                {{ 'membership.payment.pay' | translate }}
              </button>
            </div>
          }

          <div class="membership__actions">
            <button cButton color="secondary" type="button" (click)="back(paymentBackStep())">{{ 'membership.back' | translate }}</button>
          </div>
        </div>
      } @else {
        <!-- Production with no gateway yet — coming soon placeholder. -->
        <div class="membership__panel membership__coming-soon">
          <h2>{{ 'membership.payment.coming_soon_title' | translate }}</h2>
          <p>{{ 'membership.payment.coming_soon_body' | translate }}</p>
          <div class="membership__actions">
            <button cButton color="primary" type="button" (click)="goToDashboard()">
              {{ 'membership.success.dashboard' | translate }}
            </button>
          </div>
        </div>
      }
    }

    <!-- ===== Step: success ===== -->
    @if (step() === 'success') {
      <div class="membership__success">
        @if (upgradeMode() && paymentMethod() !== 'bank_transfer') {
          <h1>{{ 'membership.upgrade.success_title' | translate }}</h1>
          <p>{{ 'membership.upgrade.success_body' | translate }}</p>
        } @else if (practitionerRenewalMode() && paymentMethod() !== 'bank_transfer') {
          <h1>{{ 'membership.practitioner_renewal.success_title' | translate }}</h1>
          <p>{{ 'membership.practitioner_renewal.success_body' | translate }}</p>
        } @else if (specialUpgradeMode() && paymentMethod() !== 'bank_transfer') {
          <h1>{{ 'membership.special_upgrade.success_title' | translate }}</h1>
          <p>{{ 'membership.special_upgrade.success_body' | translate }}</p>
          <p>{{ (startMode() === 'immediate' ? 'membership.bundle.success_immediate' : 'membership.bundle.success_standard') | translate }}</p>
        } @else if (paymentMethod() === 'bank_transfer') {
          <!-- Bank transfer is not confirmed here — payment is booked manually by
               an admin once the wire arrives, so the membership is still pending. -->
          <h1>{{ 'membership.success.bank_transfer_title' | translate }}</h1>
          <p>{{ 'membership.success.bank_transfer_body' | translate }}</p>
        } @else {
          <h1>{{ 'membership.success.title' | translate }}</h1>
          <p>
            {{ (startMode() === 'immediate' ? 'membership.success.immediate' : 'membership.success.standard') | translate }}
          </p>
        }
        @if (!upgradeMode() && selectedLabel()) {
          <p><strong>{{ selectedLabel() }}</strong></p>
        }
        @if (bundleMode() && !upgradeMode()) {
          <!-- The DNA kit only ships once the withdrawal window is over — or at
               once when the member waived it (plan A3); a bank transfer first
               has to be booked. -->
          <p>
            {{ (paymentMethod() === 'bank_transfer'
                  ? 'membership.bundle.success_bank_transfer'
                  : (startMode() === 'immediate' ? 'membership.bundle.success_immediate' : 'membership.bundle.success_standard')) | translate }}
          </p>
        }
        <button cButton color="primary" (click)="goToDashboard()">{{ 'membership.success.dashboard' | translate }}</button>
      </div>
    }
  }
</div>
